// Client library for Inbound Webhook Workflows (WP-14).
import { call } from './callable';

export type WorkflowActionType =
  | 'run_flow'
  | 'add_label'
  | 'remove_label'
  | 'subscribe_sequence'
  | 'send_template'
  | 'add_to_list';

export const WORKFLOW_ACTION_TYPES: readonly WorkflowActionType[] = [
  'run_flow',
  'add_label',
  'remove_label',
  'subscribe_sequence',
  'send_template',
  'add_to_list',
] as const;

export interface WorkflowAction {
  type: WorkflowActionType;
  flowId?: string;
  templateName?: string;
  templateLanguage?: string;
  templateVariables?: string[];
  labelId?: string;
  listId?: string;
  sequenceId?: string;
  [key: string]: any;
}

export interface WebhookWorkflowDoc {
  id: string;
  name: string;
  integrationId: string;
  fieldMapping: Record<string, string>;
  actions: WorkflowAction[];
  enabled: boolean;
  stats: {
    calls: number;
    lastCalledAt: number | null;
    lastStatus: number | null;
  };
  url: string;
  createdAt: number;
  updatedAt: number;
}

export interface WebhookWorkflowCallDoc {
  id: string;
  tenantId: string;
  workflowId: string;
  at: number;
  ip: string;
  requestBodySnippet?: string | null;
  responseStatus: number;
  durationMs: number;
  error?: string | null;
  subscriberId?: string | null;
  actionsRun?: string[];
}

export interface WorkflowSaveInput {
  id?: string;
  name: string;
  integrationId: string;
  fieldMapping: Record<string, string>;
  actions: WorkflowAction[];
  enabled?: boolean;
}

export interface WorkflowSaveResult {
  ok: boolean;
  id: string;
  token?: string | null;
  secret?: string | null;
  url: string;
}

// ── Callables ────────────────────────────────────────────────────────────────

const _workflowList = call<void, { ok: boolean; workflows: WebhookWorkflowDoc[] }>('webhookWorkflowList');
const _workflowSave = call<WorkflowSaveInput, WorkflowSaveResult>('webhookWorkflowSave');
const _workflowDelete = call<{ id: string }, { ok: boolean }>('webhookWorkflowDelete');
const _workflowRotateToken = call<{ id: string }, { ok: boolean; token: string }>('webhookWorkflowRotateToken');
const _workflowRotateSecret = call<{ id: string }, { ok: boolean; secret: string }>('webhookWorkflowRotateSecret');
const _workflowCallLog = call<{ id: string; limit?: number }, { ok: boolean; calls: WebhookWorkflowCallDoc[] }>('webhookWorkflowCallLog');

// ── Functions ────────────────────────────────────────────────────────────────

export async function listWebhookWorkflows(): Promise<WebhookWorkflowDoc[]> {
  const res = await _workflowList();
  return res.data.workflows || [];
}

export async function saveWebhookWorkflow(input: WorkflowSaveInput): Promise<WorkflowSaveResult> {
  const res = await _workflowSave({
    id: input.id,
    name: input.name.trim(),
    integrationId: input.integrationId.trim() || 'primary',
    fieldMapping: input.fieldMapping || {},
    actions: input.actions || [],
    enabled: input.enabled ?? true,
  });
  return res.data;
}

export async function deleteWebhookWorkflow(id: string): Promise<void> {
  await _workflowDelete({ id });
}

export async function rotateWebhookWorkflowToken(id: string): Promise<string> {
  const res = await _workflowRotateToken({ id });
  return res.data.token;
}

export async function rotateWebhookWorkflowSecret(id: string): Promise<string> {
  const res = await _workflowRotateSecret({ id });
  return res.data.secret;
}

export async function getWebhookWorkflowCallLogs(id: string, limit = 50): Promise<WebhookWorkflowCallDoc[]> {
  const res = await _workflowCallLog({ id, limit });
  return res.data.calls || [];
}
